import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import axios from "axios";

export default function PreviewModal({ show, onHide, object, bucketName }) {
  if (!object) return null;

  const key = object.Key || "";
  const filename = decodeURIComponent(key.split("/").pop() || "");
  const [url, setUrl] = useState(object.url || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ext = useMemo(
    () => (key.includes(".") ? key.split(".").pop().toLowerCase() : ""),
    [key]
  );

  const IMAGE_EXT = new Set(["jpg","jpeg","png","gif","webp","bmp","avif","svg"]);
  const VIDEO_EXT = new Set(["mp4","m4v","webm","mov","mkv","avi"]);
  const AUDIO_EXT = new Set(["mp3","m4a","aac","wav","ogg","flac"]);

  const kind = useMemo(() => {
    if (IMAGE_EXT.has(ext)) return "image";
    if (VIDEO_EXT.has(ext)) return "video";
    if (AUDIO_EXT.has(ext)) return "audio";
    if (ext === "pdf") return "pdf";
    return "other";
  }, [ext]);

  useEffect(() => {
    if (!show || !key) return;
    if (object.url) { setUrl(object.url); setError(""); return; }

    const ctrl = new AbortController();
    const run = async () => {
      try {
        setLoading(true);
        setError("");
        setUrl("");

        const base = `${import.meta.env.VITE_BACKEND_URL}/api/share/${bucketName}/${encodeURIComponent(key)}`;
        const qs = new URLSearchParams({ expires: "600", inline: "1", redirect: "0" });
        const jwt = localStorage.getItem("token");

        const { data } = await axios.get(`${base}?${qs}`, {
          headers: { Authorization: `Bearer ${jwt}` },
          signal: ctrl.signal,
        });

        const signed = data?.url || "";
        if (!signed) throw new Error("Resposta sem URL");
        setUrl(signed);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 415) setError("Tipo de mídia não suportado para esta transformação.");
        else if (status === 403) setError("Acesso negado. Verifique a assinatura/tempo/headers.");
        else setError("Não foi possível gerar a URL de pré-visualização.");
      } finally {
        setLoading(false);
      }
    };

    run();
    return () => ctrl.abort();
  }, [show, key, bucketName, object.url]);

  const openInNewTab = () => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="d-flex align-items-center justify-content-center w-100" style={{ minHeight: 260 }}>
          <Spinner />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-3">
          <p className="mb-3">{error}</p>
          <Button variant="outline-secondary" onClick={openInNewTab} disabled={!url}>
            {url ? "Abrir em nova aba" : "Sem URL disponível"}
          </Button>
        </div>
      );
    }

    if (!url) {
      return <div className="text-center p-3">Gerando URL de pré-visualização…</div>;
    }

    if (kind === "image") {
      return (
        <div className="w-100 d-flex align-items-center justify-content-center" style={{ maxHeight: "75vh" }}>
          <img src={url} alt={filename} style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }} />
        </div>
      );
    }

    if (kind === "video") {
      // só define type quando souber com certeza (evita conflito)
      const sourceType =
        ext === "mp4" ? "video/mp4" :
        ext === "webm" ? "video/webm" :
        ext === "mov" ? "video/quicktime" : undefined;

      return (
        <div className="w-100">
          <video
            controls
            preload="metadata"
            playsInline
            style={{ width: "100%", maxHeight: "75vh" }}
          >
            {sourceType
              ? <source src={url} type={sourceType} />
              : <source src={url} /> }
            Seu navegador não suporta reprodução de vídeo.
          </video>
        </div>
      );
    }

    if (kind === "audio") {
      const sourceType =
        ext === "wav" ? "audio/wav" :
        ext === "ogg" ? "audio/ogg" :
        ext === "flac" ? "audio/flac" :
        (ext === "aac" || ext === "m4a") ? "audio/aac" : "audio/mpeg";

      return (
        <div className="w-100">
          <audio controls style={{ width: "100%" }}>
            <source src={url} type={sourceType} />
            Seu navegador não suporta reprodução de áudio.
          </audio>
        </div>
      );
    }

    if (kind === "pdf") {
      return (
        <iframe
          title={filename || "pdf-preview"}
          src={`${url}#toolbar=1&navpanes=0`}
          style={{ width: "100%", height: "75vh", border: "none" }}
        />
      );
    }

    return (
      <div className="text-center p-3">
        <p>Não foi possível gerar a pré-visualização deste tipo de arquivo.</p>
        <Button variant="outline-secondary" onClick={openInNewTab}>Baixar / Abrir</Button>
      </div>
    );
  };

  return (
    <Modal size="xl" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Pré-visualizar: {filename || object.Key}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">{renderBody()}</Modal.Body>
      <Modal.Footer>
        {url && (
          <Button variant="outline-secondary" onClick={openInNewTab}>
            Abrir em nova aba
          </Button>
        )}
        <Button variant="primary" onClick={onHide}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
}
